import axios, {AxiosResponse} from "axios";
import Customer from "@/model/Customer";
import CustomerType from "@/model/CustomerType";
import {CustomerClass} from "@/enums";
import {SpringPagedResult} from "@/service/PagedResult";
import ValidationError from "@/model/ValidationError";

const PATH = "/v2/customers";
const CUSTOMER_PAGE_SIZE = 1000;
const CUSTOMER_LOOKUP_SIZE = 10;

export enum CustomerFeatureType {
    FRUIT_AND_VEGETABLES = "FRUIT_AND_VEGETABLES",
    FLOWERS = "FLOWERS",
    STORAGE = "STORAGE",
    BEVERAGE_MARKET = "BEVERAGE_MARKET",
    TARGET_ACTUAL_COMPARISON = "TARGET_ACTUAL_COMPARISON",
    LABELS = "LABELS"
}

/**
 * A customer as the v2 API returns it. `id` is the customer's uuid; the numeric id is the deprecated `legacyId`.
 */
export interface CustomerV2Dto {
    id: string;
    legacyId: number;
    active: boolean;
    name: string;
    customerNumber?: string | null;
    customerClass: CustomerClass;
    customerType: {
        id: number;
        name: string;
    };
    region?: string | null;
    sellingArea?: string | null;
    costCenter?: string | null;
    company?: string | null;
    address: {
        street: string;
        zip?: string | null;
        city?: string | null;
    };
    phone?: string | null;
    mobile?: string | null;
    fax?: string | null;
    email?: string | null;
    auditEmail?: string | null;
    storeManager?: string | null;
    storeAssistant?: string | null;
    features: Array<{
        type: CustomerFeatureType;
        notes?: string | null;
    }>;
    additionalInformation?: string | null;
    doorCode?: string | null;
    inventoryValue?: number | null;
    positions?: number | null;
}

/**
 * The master data of a customer, as POST /v2/customers takes it and as PATCH /v2/customers/{uuid} merges it. Every
 * field is sent, a field without a value as null: a field left out of a merge patch would keep its old value.
 */
interface CustomerV2Request {
    name: string | null;
    customerNumber: string | null;
    customerClass: CustomerClass | null;
    customerTypeId: number | null;
    region: string | null;
    sellingArea: string | null;
    costCenter: string | null;
    company: string | null;
    address: {
        street: string | null;
        zip: string | null;
        city: string | null;
    };
    phone: string | null;
    mobile: string | null;
    fax: string | null;
    email: string | null;
    auditEmail: string | null;
    storeManager: string | null;
    storeAssistant: string | null;
    features: Array<CustomerV2FeatureRequest>;
    additionalInformation: string | null;
    doorCode: string | null;
    inventoryValue: number | null;
    positions: number | null;
}

interface CustomerV2FeatureRequest {
    type: CustomerFeatureType;
    notes: string | null;
}

/**
 * The fields of a {@link Customer} that edit each feature: whether it is enabled and, if the feature takes notes, its
 * notes.
 */
const FEATURE_FIELDS: Array<{type: CustomerFeatureType; enabled: keyof Customer; notes?: keyof Customer}> = [
  {type: CustomerFeatureType.FRUIT_AND_VEGETABLES, enabled: "og", notes: "notesOg"},
  {type: CustomerFeatureType.FLOWERS, enabled: "flowers", notes: "notesFlowers"},
  {type: CustomerFeatureType.STORAGE, enabled: "storage", notes: "notesStorage"},
  {type: CustomerFeatureType.BEVERAGE_MARKET, enabled: "drinks", notes: "notesDrinks"},
  {type: CustomerFeatureType.TARGET_ACTUAL_COMPARISON, enabled: "targetActualComparison"},
  {type: CustomerFeatureType.LABELS, enabled: "labels"}
];

/**
 * The form fields of the v2 request fields whose name differs. The v2 API names an invalid field after its request,
 * and it reports the address of a patched customer without the "address." prefix.
 */
const FORM_FIELDS = new Map<string, string>([
  ["customerNumber", "customerId"],
  ["doorCode", "doorcode"],
  ["street", "address.street"],
  ["zip", "address.zip"],
  ["city", "address.city"]
]);

const FEATURE_ERROR_PATH = /^features\[(\d+)]\.(notes|notesSupported)$/;

class CustomerServiceV2 {

  // REWE is the only customer type, so it is not loaded from the backend.
  getCustomerTypes(): Promise<Array<CustomerType>> {
    return Promise.resolve([new CustomerType({id: 1, name: "REWE"})]);
  }

  /**
   * The customer with the given uuid, via GET /v2/customers/{uuid}. Answers 404 if no customer has the uuid.
   */
  getCustomer(uuid: string): Promise<Customer> {
    return axios.get<CustomerV2Dto>(`${PATH}/${uuid}`)
      .then(response => this.toCustomer(response.data));
  }

  createCustomer(customer: Customer): Promise<Customer> {
    if (!customer.customerClass) {
      return Promise.reject(new Error("Customer class is required"));
    }

    return axios.post<CustomerV2Dto>(PATH, this.toRequest(customer))
      .then(response => this.toCustomer(response.data));
  }

  /**
   * Changes a customer via PATCH /v2/customers/{uuid}. The patch holds every master-data field, so the customer is
   * stored exactly as edited. Answers 409 if another customer has the name or the customer is inactive, and 422 with
   * the invalid fields, which {@link toFormErrors} maps to the form fields.
   */
  updateCustomer(customer: Customer): Promise<Customer> {
    if (!customer.uuid) {
      return Promise.reject(new Error("Customer uuid is required"));
    }

    return axios.patch<CustomerV2Dto>(`${PATH}/${customer.uuid}`, this.toRequest(customer), {
      headers: {
        "Content-Type": "application/merge-patch+json"
      }
    }).then(response => this.toCustomer(response.data));
  }

  /**
   * The validation errors of a create or update request for the customer, keyed by the form field they belong to.
   */
  toFormErrors(errors: Array<ValidationError>, customer: Customer): Map<string, string> {
    const features = this.toFeatures(customer);
    return new Map(errors.map(error => [this.toFormField(error.path, features), error.message]));
  }

  private toFormField(path: string, features: Array<CustomerV2FeatureRequest>): string {
    const featureError = FEATURE_ERROR_PATH.exec(path);
    if (featureError) {
      const feature = features[Number(featureError[1])];
      const fields = FEATURE_FIELDS.find(field => field.type === feature?.type);
      return String(fields?.notes ?? fields?.enabled ?? path);
    }
    return FORM_FIELDS.get(path) ?? path;
  }

  /**
   * Deactivates (soft-deletes) a customer via POST /v2/customers/{uuid}/deactivate. Answers 204 on success and 409 if
   * the customer is already inactive.
   */
  deactivateCustomer(uuid: string): Promise<AxiosResponse<void>> {
    return axios.post<void>(`${PATH}/${uuid}/deactivate`);
  }

  /**
   * The first customers, active or not, ordered by name, whose name, customer number, company or address contains the
   * search text, via GET /v2/customers?q=.
   */
  findCustomers(search: string): Promise<Array<Customer>> {
    return axios.get<SpringPagedResult<CustomerV2Dto>>(PATH, {
      params: {
        q: search,
        size: CUSTOMER_LOOKUP_SIZE
      }
    }).then(response => response.data.content.map(customer => this.toCustomer(customer)));
  }

  /**
   * The active customers, ordered by name, loading every page of GET /v2/customers.
   */
  findActiveCustomers(): Promise<Array<Customer>> {
    return this.findActiveCustomersFrom(0, [])
      .then(customers => customers.map(customer => this.toCustomer(customer)));
  }

  private findActiveCustomersFrom(page: number, customers: Array<CustomerV2Dto>): Promise<Array<CustomerV2Dto>> {
    return axios.get<SpringPagedResult<CustomerV2Dto>>(PATH, {
      params: {
        active: true,
        page: page,
        size: CUSTOMER_PAGE_SIZE
      }
    }).then(response => {
      const loaded = customers.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.findActiveCustomersFrom(page + 1, loaded)
        : loaded;
    });
  }

  private toCustomer(customer: CustomerV2Dto): Customer {
    const feature = (type: CustomerFeatureType) => customer.features.find(f => f.type === type);
    return new Customer({
      id: customer.legacyId,
      uuid: customer.id,
      active: customer.active,
      name: customer.name,
      customerId: customer.customerNumber,
      customerClass: customer.customerClass,
      customerType: new CustomerType(customer.customerType),
      region: customer.region,
      sellingArea: customer.sellingArea,
      costCenter: customer.costCenter,
      company: customer.company,
      address: customer.address,
      phone: customer.phone,
      mobile: customer.mobile,
      fax: customer.fax,
      email: customer.email,
      auditEmail: customer.auditEmail,
      storeManager: customer.storeManager,
      storeAssistant: customer.storeAssistant,
      og: !!feature(CustomerFeatureType.FRUIT_AND_VEGETABLES),
      notesOg: feature(CustomerFeatureType.FRUIT_AND_VEGETABLES)?.notes,
      flowers: !!feature(CustomerFeatureType.FLOWERS),
      notesFlowers: feature(CustomerFeatureType.FLOWERS)?.notes,
      storage: !!feature(CustomerFeatureType.STORAGE),
      notesStorage: feature(CustomerFeatureType.STORAGE)?.notes,
      drinks: !!feature(CustomerFeatureType.BEVERAGE_MARKET),
      notesDrinks: feature(CustomerFeatureType.BEVERAGE_MARKET)?.notes,
      targetActualComparison: !!feature(CustomerFeatureType.TARGET_ACTUAL_COMPARISON),
      labels: !!feature(CustomerFeatureType.LABELS),
      additionalInformation: customer.additionalInformation,
      doorcode: customer.doorCode,
      inventoryValue: customer.inventoryValue,
      positions: customer.positions
    });
  }

  private toRequest(customer: Customer): CustomerV2Request {
    return {
      name: textOrNull(customer.name),
      customerNumber: textOrNull(customer.customerId),
      customerClass: customer.customerClass ?? null,
      customerTypeId: customer.customerTypeId ?? null,
      region: textOrNull(customer.region),
      sellingArea: textOrNull(customer.sellingArea),
      costCenter: textOrNull(customer.costCenter),
      company: textOrNull(customer.company),
      address: {
        street: textOrNull(customer.address?.street),
        zip: textOrNull(customer.address?.zip),
        city: textOrNull(customer.address?.city)
      },
      phone: textOrNull(customer.phone),
      mobile: textOrNull(customer.mobile),
      fax: textOrNull(customer.fax),
      email: textOrNull(customer.email),
      auditEmail: textOrNull(customer.auditEmail),
      storeManager: textOrNull(customer.storeManager),
      storeAssistant: textOrNull(customer.storeAssistant),
      features: this.toFeatures(customer),
      additionalInformation: textOrNull(customer.additionalInformation),
      doorCode: textOrNull(customer.doorcode),
      inventoryValue: numberOrNull(customer.inventoryValue),
      positions: numberOrNull(customer.positions)
    };
  }

  private toFeatures(customer: Customer): Array<CustomerV2FeatureRequest> {
    return FEATURE_FIELDS
      .filter(fields => customer[fields.enabled])
      .map(fields => ({type: fields.type, notes: fields.notes ? textOrNull(customer[fields.notes]) : null}));
  }
}

/**
 * The value as text, or null if it is missing or blank. Form fields hold an empty text once they are cleared, and
 * number inputs may hold a number.
 */
function textOrNull(value: unknown): string | null {
  const text = value === undefined || value === null ? "" : `${value}`;
  return text.trim() ? text : null;
}

/**
 * The value as a number, or null if it is missing or blank.
 */
function numberOrNull(value: unknown): number | null {
  const text = textOrNull(value);
  return text === null ? null : Number(text);
}

export default new CustomerServiceV2();
